import { decToHex } from '@app-velocity/utils';

import { TextSegment, TextData } from '@app-velocity/common/types';

import { NodeWithSize, OriginalClonePair } from '../../types';
import { exportEveryTextBackgroundImage } from '../../utils/exportEveryTextBackgroundImage';
import { getImageForNode } from '../../utils/getImageForNode';
import { simpleHash } from '../../../app/utils/simple-hash';
import { isNodeWithSize } from '../../utils/isNodeWithSize';
import { findTextNodesInCopy } from '../../utils/findTextNodesInCopy';
import { isChallengeSaveError } from '../../../guards/isChallengeSaveError';
import { loadMissingFontsFromPairs } from '../general/loadMissingFonts';
import { getAbsoluteCoordinates } from '../../utils/getAbsoluteCoordinates';

type LinesData = { [nodeId: string]: { length: number }[] };

const getLineHeight = (node: TextNode, fontSize: number) => {
  const lineHeight = node.lineHeight;

  if (lineHeight === figma.mixed) {
    const segments = node.getStyledTextSegments(['lineHeight', 'fontSize']);

    return segments
      .map((segment) => getLineHeightFromSegment(segment, segment.fontSize))
      .sort((a, b) => b - a)[0];
  }

  if (lineHeight.unit === 'PERCENT') {
    return (lineHeight.value / 100) * fontSize;
  }

  if (lineHeight.unit === 'PIXELS') {
    return lineHeight.value;
  }

  return fontSize * 1.2;
};

const getLineHeightFromSegment = (
  node: Pick<StyledTextSegment, 'lineHeight'>,
  fontSize: number
) => {
  const lineHeight = node.lineHeight;

  if (lineHeight.unit === 'PERCENT') {
    return (lineHeight.value / 100) * fontSize;
  }

  if (lineHeight.unit === 'PIXELS') {
    return lineHeight.value;
  }

  return fontSize * 1.2;
};

const getFontSize = (node: TextNode) => {
  const size = node.fontSize;

  if (size === figma.mixed) {
    const segments = node.getStyledTextSegments(['fontSize']);

    return segments.map((segment) => segment.fontSize).sort((a, b) => b - a)[0];
  }

  return size;
};

const getFontWeight = (node: TextNode) => {
  const weight = node.fontWeight;

  if (weight === figma.mixed) {
    const segments = node.getStyledTextSegments(['fontWeight']);

    return segments
      .map((segment) => segment.fontWeight)
      .sort((a, b) => a - b)[0];
  }

  return weight;
};

const getTextDecoration = (node: TextNode): TextDecoration[] => {
  const textDecoration = node.textDecoration;

  if (textDecoration === figma.mixed) {
    const segments = node.getStyledTextSegments(['textDecoration']);

    return segments.map((segment) => segment.textDecoration);
  }

  return [textDecoration];
};

const getTextAlign = (node: TextNode): TextNode['textAlignHorizontal'] => {
  const textAlignment = node.textAlignHorizontal;

  return textAlignment;
};

const getSegments = (node: TextNode): TextSegment[] => {
  const segments = node.getStyledTextSegments([
    'textDecoration',
    'fontSize',
    'fontWeight',
    'fills',
    'fontSize',
    'lineHeight',
    'fillStyleId',
    'textStyleId',
  ]);

  return segments.map((segment) => ({
    start: segment.start,
    end: segment.end,
    text: segment.characters,
    textDecoration: segment.textDecoration,
    fontWeight: segment.fontWeight,
    fontSize: segment.fontSize,
    lineHeight: getLineHeightFromSegment(segment, segment.fontSize),
    fills: transformColors(segment.fills),
  }));
};

const getBoundingBox = (
  node: TextNode,
  pageNode: NodeWithSize
): Rect | null => {
  if (!node.absoluteBoundingBox) return null;

  const parentPosition = getAbsoluteCoordinates(pageNode);

  const parentX = parentPosition.x;
  const parentY = parentPosition.y;

  return {
    ...node.absoluteBoundingBox,
    x: node.absoluteBoundingBox.x - parentX,
    y: node.absoluteBoundingBox.y - parentY,
  };
};

const colorToRgb = ({ r, g, b }: RGB, opacity = 1) => {
  return `#${decToHex(r, 255)}${decToHex(g, 255)}${decToHex(b, 255)}${decToHex(
    opacity,
    255
  )}`;
};

const transformColors = (colors: readonly Paint[]): string[] => {
  const parsedColors = colors
    .filter((color) => !['VIDEO', 'IMAGE'].includes(color.type))
    .map((color) => {
      if (color.type === 'SOLID')
        return [colorToRgb(color.color, color.opacity)];
      if (color.type === 'GRADIENT_LINEAR')
        return color.gradientStops.map((colorStop) =>
          colorToRgb(colorStop.color)
        );
      if (color.type === 'GRADIENT_RADIAL')
        return color.gradientStops.map((colorStop) =>
          colorToRgb(colorStop.color)
        );
      if (color.type === 'GRADIENT_DIAMOND')
        return color.gradientStops.map((colorStop) =>
          colorToRgb(colorStop.color)
        );
      if (color.type === 'GRADIENT_ANGULAR')
        return color.gradientStops.map((colorStop) =>
          colorToRgb(colorStop.color)
        );

      return [];
    })
    .flat();

  return parsedColors;
};

const getForegroundColor = (node: BaseNode): string[] => {
  if (!('fills' in node)) return [];
  const colors: typeof figma.mixed | readonly Paint[] = node.fills;

  if (colors === figma.mixed) return [];

  return transformColors(colors);
};

const calculateWidth = (path: string): number => {
  // Split the path string into commands and coordinates
  const commands = path.split(' ').filter(Boolean);

  let minX = Infinity;
  let maxX = -Infinity;

  // Process each command in the path
  for (let i = 0; i < commands.length; i++) {
    if (!commands[i].match(/[A-Za-z]/)) {
      // Skip command letters (M, L, C, etc.)
      continue;
    }

    const toCheck = commands[i].substring(1);

    // Get the x and y values
    const x = parseFloat(toCheck);
    i++;

    if (isNaN(x)) continue;

    // Update min and max x values
    minX = Math.min(minX, x);
    maxX = Math.max(maxX, x);
  }

  // Return the width of the path
  return maxX - minX;
};

const waitForTimeout = async () => {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve(true);
    }, 100);
  });
};

const normalizeAndCloneTextNodes = async (
  textNodes: OriginalClonePair<TextNode>[]
) => {
  await loadMissingFontsFromPairs(textNodes);

  for (const node of textNodes) {
    // skipping if there is no text
    if (
      !node.clone ||
      !node.clone.characters ||
      node.clone.characters.length === 0
    ) {
      continue;
    }

    node.clone.setRangeTextDecoration(
      0,
      node.clone.characters.length,
      'UNDERLINE'
    );
    node.clone.setRangeFills(0, node.clone.characters.length, [
      figma.util.solidPaint('#000000'),
    ]);
  }

  await waitForTimeout();
};

const readLinesData = async (
  originalNodes: OriginalClonePair<TextNode>[]
): Promise<LinesData> => {
  const lines: LinesData = {};

  for (const node of originalNodes) {
    // @TODO detect if the text is a valid next - remove emojis etc
    if (node.clone.characters.length < 2) continue;

    if (node.clone.fillGeometry[1] && node.clone.fillGeometry[1].data) {
      const splitByLines = node.clone.fillGeometry[1].data.split('M');

      const [, ...properLines] = splitByLines;

      lines[node.clone.id] = properLines.map((line) => ({
        length: calculateWidth(`M${line}`),
      }));
    }
  }

  return lines;
};

export const autoResizeNodes = async (
  clonedNodes: OriginalClonePair<TextNode>[],
  linesData: LinesData
) => {
  for (const nodeClone of clonedNodes) {
    if (!nodeClone) continue;

    if (!linesData[nodeClone.clone.id]) continue;

    if (linesData[nodeClone.clone.id].length > 1) {
      nodeClone.clone.textAutoResize = 'HEIGHT';
    } else {
      nodeClone.clone.textAutoResize = 'WIDTH_AND_HEIGHT';
    }
  }

  await waitForTimeout();
};

export const getStructure = async (
  originalNode: NodeWithSize
): Promise<TextData[]> => {
  let clonedNode: NodeWithSize | null = null;

  try {
    if (!originalNode) return [];
    if (!isNodeWithSize(originalNode)) return [];
    clonedNode = originalNode.clone();
    const parsedNodes: TextData[] = [];
    const textNodes = findTextNodesInCopy(originalNode, clonedNode);

    await normalizeAndCloneTextNodes(textNodes);
    await figma.currentPage.loadAsync();
    const linesData = await readLinesData(textNodes);
    await figma.currentPage.loadAsync();
    await waitForTimeout();
    await autoResizeNodes(textNodes, linesData);
    const allTextBackgroundImagesWithoutTexts =
      await exportEveryTextBackgroundImage(textNodes);
    const allTextBackgroundImagesWithTexts =
      await exportEveryTextBackgroundImage(textNodes, true);

    for (const node of textNodes) {
      // @TODO detect if the text is a valid next - remove emojis etc
      if (node.clone.characters.length < 2) continue;

      const savedOpacity = node.clone.opacity;
      node.clone.opacity = 0;

      const imageWithoutThisText = await getImageForNode(node.clone);

      node.clone.opacity = savedOpacity;

      let isOverlapping = false;
      let isInvisible = false;

      if (
        allTextBackgroundImagesWithoutTexts &&
        !!allTextBackgroundImagesWithoutTexts[node.clone.id] &&
        allTextBackgroundImagesWithTexts &&
        !!allTextBackgroundImagesWithTexts[node.clone.id] &&
        !isChallengeSaveError(imageWithoutThisText)
      ) {
        const backgroundWithoutTextsHash = simpleHash(
          allTextBackgroundImagesWithoutTexts[node.clone.id]!
        );

        const backgroundWithTextsHash = simpleHash(
          allTextBackgroundImagesWithTexts[node.clone.id]!
        );

        const backgroundWithoutThisTextHash = simpleHash(
          imageWithoutThisText.converted.take()
        );

        isOverlapping =
          backgroundWithoutTextsHash !== backgroundWithoutThisTextHash;
        isInvisible = backgroundWithTextsHash === backgroundWithoutThisTextHash;
      }

      if (isInvisible) continue;

      const fontSize = getFontSize(node.clone);

      parsedNodes.push({
        name: node.original.name,
        id: node.original.id,
        text: node.original.characters,
        lines: linesData[node.clone.id] || [],
        size: fontSize,
        height: node.clone.height,
        weight: getFontWeight(node.original),
        lineHeight: getLineHeight(node.original, fontSize),
        foregroundColors: getForegroundColor(node.original),
        decoration: getTextDecoration(node.original),
        boundingBox: getBoundingBox(
          node.original,
          originalNode as NodeWithSize
        ),
        alignment: getTextAlign(node.original),
        isOverlapping,
        segments: getSegments(node.original),
      } as TextData);
    }

    if (clonedNode) clonedNode.remove();

    return parsedNodes;
  } catch (e) {
    if (clonedNode) clonedNode.remove();
    throw e;
  }
};
